import { WORD_LENGTH, type GuessEntry, type LetterState } from "@mk/shared";
import { shareUrl } from "./shareText.ts";
import { gameEdition, upperLabel } from "../../lib/edition.ts";
import { messages } from "../../i18n/index.ts";

/**
 * Paylaşılabilir sonuç görseli.
 *
 * Tamamen istemcide, canvas ile çiziliyor: sunucuya iş düşmüyor, çevrimdışı
 * pratik oyununda bile çalışıyor ve oyuncunun tahtası hiçbir yere gönderilmiyor.
 *
 * Kare (1080x1080) seçildi — WhatsApp, Instagram ve X'te kırpılmadan duran tek
 * ölçü bu.
 */

const SIZE = 1080;
const PAD = 84;

/** Karanlık zemin: paylaşılan görsel oyuncunun temasına göre değişmemeli. */
const INK = {
  bg: "#0a0a0f",
  panel: "#13131b",
  text: "#f2f0ea",
  muted: "#8b8b9e",
  correct: "#1ac8a0",
  correctInk: "#04120d",
  present: "#ffc24d",
  presentInk: "#1a1200",
  absent: "#262633",
  absentInk: "#6e6e82",
  mint: "#1ac8a0",
} as const;

const FILL: Record<LetterState, string> = {
  correct: INK.correct,
  present: INK.present,
  absent: INK.absent,
};

const FILL_INK: Record<LetterState, string> = {
  correct: INK.correctInk,
  present: INK.presentInk,
  absent: INK.absentInk,
};

/** Görseldeki tek bir tahta. */
export interface ShareCardBoard {
  /** Üstünde yazacak ad; tek tahtalı görselde verilmez. */
  label?: string;
  rows: readonly GuessEntry[];
}

export interface ShareCardInput {
  eyebrow: string;
  message: string;
  rows: readonly GuessEntry[];
  /**
   * Düelloda rakibin tahtası. Verilirse görsel iki tahtayı yan yana çiziyor —
   * "kim kaç tahminde bulmuş" tek bakışta görünüyor, paylaşımın bütün anlamı o.
   * Maç bittiği için rakibin harflerini göstermenin sakıncası yok.
   */
  opponent?: ShareCardBoard;
  /** İki tahtalı görselde kendi tahtanın üstündeki ad. */
  youLabel?: string;
  /**
   * Kutucuklara harfler yazılsın mı?
   *
   * Günlük kelimede varsayılan HAYIR: görseli gören ama o günü henüz oynamamış
   * kişiye cevabı vermiş oluruz. Pratik ve düelloda kelime herkese özel olduğu
   * için harf göstermenin sakıncası yok.
   */
  showLetters: boolean;
  /**
   * 3 dakika koşusunda tahta yerine özet çizilir: büyük kelime sayısı ve her
   * tamamlanan kelime için bir kare (bulunduysa deneme sayısı, kaçtıysa ×).
   */
  summary?: ShareCardSummary;
}

export interface ShareCardSummary {
  value: string;
  caption: string;
  chips: readonly { solved: boolean; label: string }[];
}

/** Özetin kare ızgarası en fazla bu kadar sütuna yayılır. */
const SUMMARY_COLUMNS = 10;

function drawSummary(ctx: CanvasRenderingContext2D, summary: ShareCardSummary, top: number, bottom: number): void {
  const width = SIZE - PAD * 2;
  const valueSize = 230;
  const captionSize = 32;
  const chipGap = 12;
  const columns = Math.max(1, Math.min(SUMMARY_COLUMNS, summary.chips.length));
  const chipRows = Math.ceil(summary.chips.length / columns);
  const headHeight = valueSize + 24 + captionSize;
  const chipSpace = Math.max(0, bottom - top - headHeight - 48);
  const chip = chipRows === 0 ? 0 : Math.min(
    84,
    (width - chipGap * (columns - 1)) / columns,
    (chipSpace - chipGap * (chipRows - 1)) / chipRows,
  );
  const chipsHeight = chipRows === 0 ? 0 : chip * chipRows + chipGap * (chipRows - 1) + 48;
  const startY = top + (bottom - top - headHeight - chipsHeight) / 2;

  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillStyle = INK.mint;
  ctx.font = `400 ${valueSize}px Anton, system-ui, sans-serif`;
  ctx.fillText(summary.value, SIZE / 2, startY);

  ctx.fillStyle = INK.muted;
  ctx.font = `700 ${captionSize}px 'Sora Variable', system-ui, sans-serif`;
  ctx.letterSpacing = "4px";
  ctx.fillText(summary.caption, SIZE / 2, startY + valueSize + 24, width);
  ctx.letterSpacing = "0px";

  if (chipRows === 0) return;
  const chipsTop = startY + headHeight + 48;
  ctx.textBaseline = "middle";
  summary.chips.forEach((item, index) => {
    const row = Math.floor(index / columns);
    const inRow = Math.min(columns, summary.chips.length - row * columns);
    const rowWidth = chip * inRow + chipGap * (inRow - 1);
    const x = (SIZE - rowWidth) / 2 + (index % columns) * (chip + chipGap);
    const y = chipsTop + row * (chip + chipGap);
    roundRect(ctx, x, y, chip, chip, Math.max(6, Math.round(chip * 0.14)));
    ctx.fillStyle = item.solved ? INK.correct : INK.absent;
    ctx.fill();
    if (!item.solved) {
      // Yazı tipinde × çok ince kalıyor; çarpıyı çizgiyle çiziyoruz.
      const inset = chip * 0.32;
      ctx.strokeStyle = INK.absentInk;
      ctx.lineWidth = Math.max(3, chip * 0.08);
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(x + inset, y + inset);
      ctx.lineTo(x + chip - inset, y + chip - inset);
      ctx.moveTo(x + chip - inset, y + inset);
      ctx.lineTo(x + inset, y + chip - inset);
      ctx.stroke();
      return;
    }
    ctx.fillStyle = INK.correctInk;
    ctx.font = `400 ${Math.round(chip * 0.55)}px Anton, system-ui, sans-serif`;
    ctx.fillText(item.label, x + chip / 2, y + chip / 2 + chip * 0.03);
  });
}

/**
 * Yazı tiplerini hazırlar.
 *
 * Canvas, belgede yüklü olmayan bir yazı tipini sessizce sistem yazı tipiyle
 * değiştirir; görsel o zaman uygulamaya benzemez. Yükleme başarısız olursa
 * çizim yine de sürüyor — görsel üretmemekten iyi.
 */
async function ensureFonts(): Promise<void> {
  if (!("fonts" in document)) return;
  try {
    await Promise.all([
      document.fonts.load("400 96px Anton"),
      document.fonts.load("600 34px 'Sora Variable'"),
      document.fonts.load("700 30px 'Sora Variable'"),
    ]);
  } catch {
    /* sistem yazı tipiyle devam */
  }
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** Uzun mesajı kutuya sığdırır; satır satır döner. */
function wrap(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(" ");
  const lines: string[] = [];
  let line = "";
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (ctx.measureText(candidate).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  }
  if (line) lines.push(line);
  return lines;
}

export async function renderShareCard(input: ShareCardInput): Promise<Blob> {
  await ensureFonts();

  const canvas = document.createElement("canvas");
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error(messages.share.imageFailed);

  ctx.fillStyle = INK.bg;
  ctx.fillRect(0, 0, SIZE, SIZE);

  /* --- Üst şerit: marka --------------------------------------------- */

  ctx.fillStyle = INK.mint;
  ctx.fillRect(0, 0, SIZE, 10);

  ctx.textBaseline = "top";
  ctx.fillStyle = INK.muted;
  ctx.font = "700 26px 'Sora Variable', system-ui, sans-serif";
  ctx.letterSpacing = "6px";
  const brandWords = gameEdition.productName.replace("+", "").split(/\s+/);
  const brandTop = brandWords.length > 1 ? brandWords.slice(0, -1).join(" ") : brandWords[0]!;
  const brandBottom = brandWords.at(-1)!;
  ctx.fillText(upperLabel(brandTop), PAD, PAD);

  ctx.letterSpacing = "0px";
  ctx.fillStyle = INK.text;
  ctx.font = "400 76px Anton, system-ui, sans-serif";
  const titleY = PAD + 40;
  ctx.fillText(upperLabel(brandBottom), PAD, titleY);
  const titleWidth = ctx.measureText(upperLabel(brandBottom)).width;
  ctx.fillStyle = INK.mint;
  if (gameEdition.productName.includes("+")) ctx.fillText("+", PAD + titleWidth + 8, titleY);

  /* --- Mod ve gün ---------------------------------------------------- */

  ctx.fillStyle = INK.mint;
  ctx.font = "700 28px 'Sora Variable', system-ui, sans-serif";
  ctx.letterSpacing = "4px";
  ctx.fillText(input.eyebrow, PAD, titleY + 100);
  ctx.letterSpacing = "0px";

  /* --- Tahtalar ------------------------------------------------------ */

  if (input.summary) {
    drawSummary(ctx, input.summary, titleY + 168, SIZE - PAD - 150);
  } else {
    const boards: ShareCardBoard[] = input.opponent
      ? [{ label: input.youLabel ?? messages.common.you, rows: input.rows }, input.opponent]
      : [{ rows: input.rows }];

    const boardTop = titleY + 168;
    const boardBottom = SIZE - PAD - 150;
    const available = boardBottom - boardTop;
    const labelSpace = input.opponent ? 46 : 0;

    const gap = input.opponent ? 8 : 14;
    const columnGap = 56;
    const rowCount = Math.max(1, ...boards.map((board) => board.rows.length));

    // Kutucuk hem genişliğe hem yüksekliğe sığmalı; küçük olanı seçiyoruz.
    const usableWidth = SIZE - PAD * 2 - (boards.length - 1) * columnGap;
    const byWidth =
      (usableWidth / boards.length - gap * (WORD_LENGTH - 1)) / WORD_LENGTH;
    const byHeight = (available - labelSpace - gap * (rowCount - 1)) / rowCount;
    const tile = Math.min(byWidth, byHeight, 150);

    const boardWidth = tile * WORD_LENGTH + gap * (WORD_LENGTH - 1);
    const boardHeight = tile * rowCount + gap * (rowCount - 1);
    const totalWidth = boardWidth * boards.length + columnGap * (boards.length - 1);
    const firstX = (SIZE - totalWidth) / 2;
    const startY = boardTop + labelSpace + (available - labelSpace - boardHeight) / 2;

    boards.forEach((board, index) => {
      const startX = firstX + index * (boardWidth + columnGap);

      if (board.label) {
        const label = upperLabel(board.label);
        ctx.textAlign = "center";
        ctx.textBaseline = "alphabetic";
        ctx.fillStyle = INK.muted;
        ctx.letterSpacing = "2px";
        let labelSize = 28;
        do {
          ctx.font = `700 ${labelSize}px 'Sora Variable', system-ui, sans-serif`;
          if (ctx.measureText(label).width <= boardWidth) break;
          labelSize -= 1;
        } while (labelSize >= 18);
        ctx.fillText(label, startX + boardWidth / 2, startY - 18, boardWidth);
        ctx.letterSpacing = "0px";
      }

      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      board.rows.forEach((row, r) => {
        const letters = [...row.word];
        row.pattern.forEach((state, c) => {
          const x = startX + c * (tile + gap);
          const y = startY + r * (tile + gap);

          roundRect(ctx, x, y, tile, tile, Math.max(6, Math.round(tile * 0.11)));
          ctx.fillStyle = FILL[state];
          ctx.fill();

          if (input.showLetters) {
            ctx.fillStyle = FILL_INK[state];
            ctx.font = `400 ${Math.round(tile * 0.58)}px Anton, system-ui, sans-serif`;
            ctx.fillText(letters[c] ?? "", x + tile / 2, y + tile / 2 + tile * 0.04);
          }
        });
      });
    });
  }

  /* --- Mesaj ve adres ------------------------------------------------ */

  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillStyle = INK.text;
  ctx.font = "600 34px 'Sora Variable', system-ui, sans-serif";

  const lines = wrap(ctx, input.message, SIZE - PAD * 2);
  const messageTop = SIZE - PAD - 116 - (lines.length - 1) * 44;
  lines.forEach((line, i) => {
    ctx.fillText(line, PAD, messageTop + i * 44);
  });

  ctx.fillStyle = INK.muted;
  ctx.font = "700 26px 'Sora Variable', system-ui, sans-serif";
  ctx.letterSpacing = "3px";
  ctx.fillText(upperLabel(shareUrl()), PAD, SIZE - PAD - 34);
  ctx.letterSpacing = "0px";

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error(messages.share.imageFailed))),
      "image/png",
    );
  });
}
